import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCurrency } from "@/lib/utils";

import { formatShrinkagePercentage } from "./repacking-utils";

import type { RepackShrinkageItem } from "@artisancode/api-types";

interface Props {
  items: RepackShrinkageItem[];
}

const HEAD = "h-10 text-xs font-medium";

export function RepackShrinkageTable({ items }: Props) {
  return (
    <div className="relative w-full overflow-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/40 hover:bg-muted/40">
            <TableHead className={HEAD}>Bahan Baku (Resep)</TableHead>
            <TableHead className={HEAD}>Satuan</TableHead>
            <TableHead className={`${HEAD} text-right`}>Target (Exp)</TableHead>
            <TableHead className={`${HEAD} text-right`}>Hasil Aktual</TableHead>
            <TableHead className={`${HEAD} text-right`}>Total Susut</TableHead>
            <TableHead className={`${HEAD} text-center`}>% Susut</TableHead>
            <TableHead className={`${HEAD} text-right`}>Nilai Susut</TableHead>
            <TableHead className={`${HEAD} text-center pr-4`}>
              Jml Dokumen
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={8}
                className="h-32 text-center text-xs text-muted-foreground"
              >
                Belum ada data susut repacking pada periode atau filter ini.
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => {
              const hasShrinkage = item.shrinkageQty > 0;
              const isHigh = item.shrinkagePercentage > 5;

              return (
                <TableRow
                  key={item.rawMaterialId}
                  className="hover:bg-muted/20"
                >
                  <TableCell className="py-2.5">
                    <div className="flex flex-col">
                      <span className="text-xs font-medium text-foreground">
                        {item.rawMaterialName}
                      </span>
                      {item.rawMaterialSku && (
                        <span className="text-[11px] font-mono text-muted-foreground">
                          {item.rawMaterialSku}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-2.5 text-xs text-muted-foreground">
                    {item.uomName}
                  </TableCell>
                  <TableCell className="py-2.5 text-right text-xs font-medium text-muted-foreground">
                    {item.expectedQty.toLocaleString("id-ID")}
                  </TableCell>
                  <TableCell className="py-2.5 text-right text-xs font-medium">
                    {item.actualQty.toLocaleString("id-ID")}
                  </TableCell>
                  <TableCell className="py-2.5 text-right text-xs font-medium">
                    <span
                      className={
                        hasShrinkage
                          ? "text-rose-600 dark:text-rose-400 font-semibold"
                          : "text-muted-foreground"
                      }
                    >
                      {item.shrinkageQty.toLocaleString("id-ID")} {item.uomName}
                    </span>
                  </TableCell>
                  <TableCell className="py-2.5 text-center">
                    {hasShrinkage ? (
                      <Badge
                        variant="secondary"
                        className={
                          isHigh
                            ? "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30 text-[11px] font-semibold"
                            : "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 text-[11px] font-semibold"
                        }
                      >
                        {formatShrinkagePercentage(item.shrinkagePercentage)}
                      </Badge>
                    ) : (
                      <Badge
                        variant="secondary"
                        className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20 text-[11px]"
                      >
                        0.0%
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="py-2.5 text-right text-xs font-semibold text-rose-600 dark:text-rose-400">
                    {item.shrinkageValue > 0
                      ? formatCurrency(item.shrinkageValue)
                      : "—"}
                  </TableCell>
                  <TableCell className="py-2.5 text-center text-xs text-muted-foreground pr-4">
                    {item.documentCount} repack
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
